import React, { useEffect, useState } from 'react';
import axiosAuth from '../../../config/axios';
import { fecha, ahoraLocal, estilos as s } from '../comun';
import { aInputLocal } from '../citas';

// Pestaña "Familia" del caso: registro de comunicaciones con los acudientes.
// Lo marcado como visible lo ve la familia en su portal (si el colegio lo activó):
// la fecha, el medio y el resultado. Nunca el motivo.

function vacio() {
  return { fecha: ahoraLocal(), tipo_id: '', acudiente_id: '', acudiente_texto: '', motivo: '', resultado: '', visible_familia: false };
}

function Formulario({ caso, tipos, acudientes, contacto, onListo, onCancelar }) {
  const [f, setF] = useState(contacto ? {
    fecha: aInputLocal(contacto.fecha), tipo_id: contacto.tipo_id || '', acudiente_id: contacto.acudiente_id || '',
    acudiente_texto: contacto.acudiente_texto || '', motivo: contacto.motivo || '', resultado: contacto.resultado || '',
    visible_familia: contacto.visible_familia,
  } : { ...vacio(), acudiente_id: acudientes[0]?.id || '' });
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const set = (campo) => (e) => setF(x => ({ ...x, [campo]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const sinCuenta = !f.acudiente_id;

  async function guardar(e) {
    e.preventDefault();
    setError('');
    if (!f.tipo_id) return setError('Selecciona el tipo de contacto');
    if (sinCuenta && !f.acudiente_texto.trim()) return setError('Indica con quién fue el contacto');
    if (!f.resultado.trim()) return setError('Registra el resultado del contacto');
    const cuerpo = {
      fecha: f.fecha, tipo_id: parseInt(f.tipo_id), motivo: f.motivo, resultado: f.resultado, visible_familia: f.visible_familia,
      acudiente_id: f.acudiente_id ? parseInt(f.acudiente_id) : null, acudiente_texto: sinCuenta ? f.acudiente_texto : '',
    };
    setOcupado(true);
    try {
      if (contacto) await axiosAuth.patch(`/api/bienestar/contactos-familia/${contacto.id}`, cuerpo);
      else await axiosAuth.post(`/api/bienestar/casos/${caso.id}/contactos-familia`, cuerpo);
      onListo();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el contacto');
      setOcupado(false);
    }
  }

  return (
    <form onSubmit={guardar} style={es.formulario}>
      <h4 style={es.formTitulo}>{contacto ? 'Editar contacto' : 'Registrar contacto con la familia'}</h4>
      <div style={es.fila2}>
        <div style={s.campo}>
          <label style={s.label}>Fecha y hora *</label>
          <input type="datetime-local" style={s.input} value={f.fecha} onChange={set('fecha')} required />
        </div>
        <div style={s.campo}>
          <label style={s.label}>Medio *</label>
          <select style={s.input} value={f.tipo_id} onChange={set('tipo_id')}>
            <option value="">— Selecciona —</option>
            {tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>
      </div>
      <div style={es.fila2}>
        <div style={s.campo}>
          <label style={s.label}>Acudiente *</label>
          <select style={s.input} value={f.acudiente_id} onChange={set('acudiente_id')}>
            {acudientes.map(a => <option key={a.id} value={a.id}>{a.nombre}{a.parentesco ? ` (${a.parentesco})` : ''}</option>)}
            <option value="">Otra persona (sin cuenta en Playfesor)</option>
          </select>
        </div>
        {sinCuenta && (
          <div style={s.campo}>
            <label style={s.label}>¿Con quién? *</label>
            <input style={s.input} maxLength={150} value={f.acudiente_texto} onChange={set('acudiente_texto')} placeholder="Ej.: Abuela materna" />
          </div>
        )}
      </div>
      <div style={s.campo}>
        <label style={s.label}>Motivo <span style={es.pista}>(solo lo ve orientación)</span></label>
        <input style={s.input} maxLength={4000} value={f.motivo} onChange={set('motivo')} />
      </div>
      <div style={s.campo}>
        <label style={s.label}>Resultado *</label>
        <textarea style={{ ...s.input, minHeight: '80px', resize: 'vertical' }} maxLength={4000} value={f.resultado} onChange={set('resultado')}
          placeholder="Qué se habló y qué se acordó." />
      </div>
      {caso.permisos.portal_familia && (
        <label style={es.check}>
          <input type="checkbox" checked={f.visible_familia} onChange={set('visible_familia')} />
          Visible para la familia: el acudiente verá la fecha, el medio y el <strong>resultado</strong>. Escríbelo pensando en que lo leerá.
        </label>
      )}
      {error && <div style={s.errorBox}>{error}</div>}
      <div style={s.botones}>
        <button type="button" onClick={onCancelar} style={s.btnSecundario}>Cancelar</button>
        <button type="submit" disabled={ocupado} style={s.btnPrimario}>{ocupado ? 'Guardando...' : 'Guardar'}</button>
      </div>
    </form>
  );
}

export default function Familia({ caso, onCambio }) {
  const [tipos, setTipos] = useState([]);
  const [acudientes, setAcudientes] = useState(null);
  const [editando, setEditando] = useState(null);     // null | 'nuevo' | contacto
  const editable = caso.permisos.editable;

  useEffect(() => {
    axiosAuth.get('/api/bienestar/catalogos?tipo=tipo_contacto').then(r => setTipos((r.data.data || []).filter(t => t.activo))).catch(() => {});
    axiosAuth.get(`/api/bienestar/casos/${caso.id}/acudientes`).then(r => setAcudientes(r.data.data || [])).catch(() => setAcudientes([]));
  }, [caso.id]);

  const contactos = caso.contactos_familia || [];
  return (
    <div>
      {acudientes && (
        <div style={es.acudientes}>
          <strong>Acudientes con cuenta:</strong>{' '}
          {acudientes.length ? acudientes.map(a => `${a.nombre}${a.parentesco ? ` (${a.parentesco})` : ''}`).join(' · ') : 'ninguno vinculado'}
          {!caso.permisos.portal_familia && <div style={es.pista}>El portal de familia para orientación está desactivado en el colegio: la familia no ve nada de este módulo.</div>}
        </div>
      )}
      {editable && !editando && (
        <button onClick={() => setEditando('nuevo')} style={{ ...s.btnPrimario, marginBottom: '14px' }}>+ Registrar contacto</button>
      )}
      {editando && acudientes && (
        <Formulario key={editando === 'nuevo' ? 'nuevo' : editando.id} caso={caso} tipos={tipos}
          acudientes={acudientes} contacto={editando === 'nuevo' ? null : editando}
          onCancelar={() => setEditando(null)} onListo={() => { setEditando(null); onCambio(); }} />
      )}
      {contactos.length === 0 && !editando && <div style={es.vacio}>No hay contactos con la familia registrados.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {contactos.map(c => (
          <div key={c.id} style={es.tarjeta}>
            <div style={es.cabecera}>
              <div>
                <div style={es.titulo}>{c.tipo || 'Contacto'} · {c.acudiente || 'Acudiente'}</div>
                <div style={es.meta}>{fecha(c.fecha, true)}{c.responsable ? ` · ${c.responsable}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {c.visible_familia && <span style={es.visible}>Visible para la familia</span>}
                {editable && !editando && <button onClick={() => setEditando(c)} style={es.btnMini}>Editar</button>}
              </div>
            </div>
            {c.motivo && <div style={es.linea}><span style={es.lineaTitulo}>Motivo</span>{c.motivo}</div>}
            <div style={es.linea}><span style={es.lineaTitulo}>Resultado</span>{c.resultado}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const es = {
  acudientes: { background: '#fafbff', border: '1px solid #e6e8f5', borderRadius: '12px', padding: '10px 14px', fontSize: '13px', color: '#555', marginBottom: '14px', lineHeight: 1.5 },
  formulario: { background: '#fafbff', border: '1px solid #e6e8f5', borderRadius: '14px', padding: '16px', marginBottom: '16px' },
  formTitulo: { fontSize: '15px', fontWeight: 800, color: '#1a1a2e', margin: '0 0 12px' },
  fila2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '12px' },
  pista: { fontSize: '11.5px', fontWeight: 500, color: '#999', marginTop: '2px' },
  check: { display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: '#555', marginBottom: '14px', cursor: 'pointer', lineHeight: 1.4 },
  tarjeta: { background: '#fff', border: '1px solid #eef0f5', borderRadius: '12px', padding: '14px 16px' },
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' },
  titulo: { fontSize: '14.5px', fontWeight: 700, color: '#1a1a2e' },
  meta: { fontSize: '12px', color: '#999', marginTop: '2px' },
  visible: { background: '#efedff', color: '#5a4fcf', borderRadius: '999px', padding: '3px 10px', fontSize: '11.5px', fontWeight: 700 },
  btnMini: { background: '#fff', border: '1px solid #dde0f0', color: '#667eea', borderRadius: '8px', padding: '6px 12px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  linea: { fontSize: '14px', color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginTop: '10px' },
  lineaTitulo: { display: 'block', fontSize: '11px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.4px' },
  vacio: { background: '#fafbff', border: '1px dashed #dde0f0', borderRadius: '12px', padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' },
};
